import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { CustomerLayout } from "@/components/CustomerLayout";
import { OrderTicket, STATUS_LABELS, STATUS_COLORS } from "@/components/OrderTicket";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Circle } from "lucide-react";

const FLOW = ["received", "confirmed", "preparing", "packing", "ready", "out_for_delivery", "delivered", "completed"];

export function Timeline({ order }) {
  const idx = FLOW.indexOf(order.status);
  const cancelled = ["cancelled", "failed", "refunded"].includes(order.status);
  if (cancelled) {
    return <div className="bg-white rounded-2xl border p-5" data-testid="timeline">
      <div className="flex items-center gap-2 text-[#EF4444] font-semibold"><Circle size={18} fill="#EF4444" /> {STATUS_LABELS[order.status]}</div>
    </div>;
  }
  return (
    <div className="bg-white rounded-2xl border p-5" data-testid="timeline">
      <h3 className="font-semibold mb-4">Order Progress</h3>
      <div className="space-y-0">
        {FLOW.map((s, i) => {
          const done = i <= idx;
          const hist = order.status_history?.find((h) => h.status === s);
          return (
            <div key={s} className="flex gap-3" data-testid={`timeline-${s}`}>
              <div className="flex flex-col items-center">
                {done ? <CheckCircle2 size={22} style={{ color: STATUS_COLORS[s] }} /> : <Circle size={22} className="text-muted-foreground/30" />}
                {i < FLOW.length - 1 && <div className={`w-0.5 flex-1 min-h-6 ${i < idx ? "bg-[#1B5E20]" : "bg-muted"}`} />}
              </div>
              <div className={`pb-4 ${done ? "" : "opacity-40"}`}>
                <div className="font-medium text-sm">{STATUS_LABELS[s]}</div>
                {hist && <div className="text-xs text-muted-foreground">{new Date(hist.at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })} · {hist.by}</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function OrderConfirmation() {
  const { id } = useParams();
  const nav = useNavigate();
  const [order, setOrder] = useState(null);

  useEffect(() => {
    const load = () => api.get(`/orders/${id}`).then((r) => setOrder(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [id]);

  if (!order) return <CustomerLayout><div className="py-20 text-center text-muted-foreground">Loading…</div></CustomerLayout>;

  return (
    <CustomerLayout>
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="text-center mb-6 as-pop">
          <div className="inline-grid place-items-center h-16 w-16 rounded-full bg-[#E8F5E9] mb-3"><CheckCircle2 className="text-[#1B5E20]" size={36} /></div>
          <h1 className="font-display text-2xl font-bold">Thank you for your order!</h1>
          <p className="text-muted-foreground">We've received it and the store has been notified.</p>
        </div>
        <div className="space-y-4">
          <OrderTicket order={order} />
          <Timeline order={order} />
          <div className="grid grid-cols-2 gap-3">
            <Button variant="outline" onClick={() => nav("/my-orders")} data-testid="to-my-orders">My Orders</Button>
            <Button className="bg-[#1B5E20] hover:bg-[#144718]" onClick={() => nav("/")} data-testid="order-again-btn">Order Again</Button>
          </div>
        </div>
      </div>
    </CustomerLayout>
  );
}
